/**
 * MeasurementManager — Autodesk-style measurement overlay for the inspector viewer.
 * Tools: distance (2 points), laser (surface cross through the click point),
 * angle (3 points). Multiple measurements persist; one can be selected/deleted.
 */
import * as THREE from "three";
import { LineSegments2 } from "three/examples/jsm/lines/LineSegments2.js";
import { LineSegmentsGeometry } from "three/examples/jsm/lines/LineSegmentsGeometry.js";
import { LineMaterial } from "three/examples/jsm/lines/LineMaterial.js";

export const MEASURE_UNITS = {
  mm: { factor: 1, label: "mm" },
  cm: { factor: 0.1, label: "cm" },
  m: { factor: 0.001, label: "m" },
  in: { factor: 1 / 25.4, label: "in" },
};

export const DEFAULT_MEASURE_SETTINGS = { unit: "mm", decimals: 1 };

const BLUE = 0x2563eb;
const PREVIEW = 0x60a5fa;
const SELECTED = 0xf59e0b;
const HEX_BLUE = "#2563EB";

export function formatLengthMM(mm, settings) {
  const unit = MEASURE_UNITS[settings.unit] || MEASURE_UNITS.mm;
  const value = mm * unit.factor;
  return `${value.toLocaleString("en-US", {
    minimumFractionDigits: settings.decimals,
    maximumFractionDigits: settings.decimals,
  })} ${unit.label}`;
}

export function formatAngleDeg(deg, settings) {
  return `${deg.toLocaleString("en-US", {
    minimumFractionDigits: settings.decimals,
    maximumFractionDigits: settings.decimals,
  })}°`;
}

/** White pill with blue text, constant screen size (Autodesk-style label). */
class MeasureLabel {
  constructor(text) {
    this.canvas = document.createElement("canvas");
    this.canvas.width = 512;
    this.canvas.height = 128;
    this.ctx = this.canvas.getContext("2d");
    this.texture = new THREE.CanvasTexture(this.canvas);
    this.sprite = new THREE.Sprite(
      new THREE.SpriteMaterial({
        map: this.texture,
        depthTest: false,
        transparent: true,
        sizeAttenuation: false,
      })
    );
    this.sprite.scale.set(0.11, 0.0275, 1);
    this.sprite.renderOrder = 1000;
    this.sprite.userData.label = true;
    this.setText(text || "");
  }

  setText(text) {
    const ctx = this.ctx;
    ctx.clearRect(0, 0, 512, 128);
    ctx.beginPath();
    if (ctx.roundRect) ctx.roundRect(8, 22, 496, 84, 20);
    else ctx.rect(8, 22, 496, 84);
    ctx.fillStyle = "#FFFFFF";
    ctx.fill();
    ctx.lineWidth = 4;
    ctx.strokeStyle = HEX_BLUE;
    ctx.stroke();
    ctx.fillStyle = HEX_BLUE;
    ctx.font = "bold 52px monospace";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText(text, 256, 66);
    this.texture.needsUpdate = true;
  }

  dispose() {
    this.texture.dispose();
    this.sprite.material.dispose();
  }
}

export class MeasurementManager {
  constructor(viewer) {
    this.viewer = viewer;
    this.tool = "distance";
    this.active = false;
    this.settings = { ...DEFAULT_MEASURE_SETTINGS };
    this.items = new Map();
    this.selectedId = null;
    this.pending = [];
    this._nextId = 1;
    this._laserAt = 0;

    this.group = new THREE.Group();
    this.previewGroup = new THREE.Group();
    this.pendingGroup = new THREE.Group();
    viewer.scene.add(this.group, this.previewGroup, this.pendingGroup);

    this._res = new THREE.Vector2(1, 1);
    this.setResolution(
      viewer.renderer.domElement.clientWidth || 1,
      viewer.renderer.domElement.clientHeight || 1
    );
    this._probe = new THREE.Raycaster();
    this._probe.firstHitOnly = true; // three-mesh-bvh fast path

    this._previewLabels = [new MeasureLabel(""), new MeasureLabel("")];
    this._previewLabels.forEach((label) => {
      label.sprite.visible = false;
      label.sprite.userData.shared = true;
      this.previewGroup.add(label.sprite);
    });
    this._snapSprite = null;
    this._tex = null;
  }

  // ---------- shared drawing ----------

  _textures() {
    if (this._tex) return this._tex;
    const make = (draw) => {
      const canvas = document.createElement("canvas");
      canvas.width = 128;
      canvas.height = 128;
      draw(canvas.getContext("2d"));
      return new THREE.CanvasTexture(canvas);
    };
    this._tex = {
      marker: make((ctx) => {
        ctx.beginPath();
        ctx.arc(64, 64, 46, 0, Math.PI * 2);
        ctx.fillStyle = "#FFFFFF";
        ctx.fill();
        ctx.lineWidth = 12;
        ctx.strokeStyle = HEX_BLUE;
        ctx.stroke();
        ctx.beginPath();
        ctx.arc(64, 64, 12, 0, Math.PI * 2);
        ctx.fillStyle = HEX_BLUE;
        ctx.fill();
      }),
      snapVertex: make((ctx) => {
        ctx.lineWidth = 12;
        ctx.strokeStyle = "#D97706";
        ctx.strokeRect(26, 26, 76, 76);
        ctx.fillStyle = "rgba(255,255,255,0.85)";
        ctx.fillRect(34, 34, 60, 60);
      }),
      snapEdge: make((ctx) => {
        ctx.lineWidth = 12;
        ctx.strokeStyle = "#D97706";
        ctx.beginPath();
        ctx.moveTo(64, 20);
        ctx.lineTo(108, 64);
        ctx.lineTo(64, 108);
        ctx.lineTo(20, 64);
        ctx.closePath();
        ctx.stroke();
      }),
    };
    return this._tex;
  }

  _lineSegments(flatPoints, color) {
    const geometry = new LineSegmentsGeometry();
    const flat = [];
    flatPoints.forEach((p) => flat.push(p.x, p.y, p.z));
    geometry.setPositions(flat);
    const material = new LineMaterial({
      color,
      linewidth: 2,
      depthTest: false,
      transparent: true,
    });
    material.resolution.copy(this._res);
    const line = new LineSegments2(geometry, material);
    line.renderOrder = 998;
    return line;
  }

  _linePolyline(points, color) {
    const pairs = [];
    for (let i = 0; i + 1 < points.length; i += 1) pairs.push(points[i], points[i + 1]);
    return this._lineSegments(pairs, color);
  }

  _marker(point, scale = 0.018) {
    const sprite = new THREE.Sprite(
      new THREE.SpriteMaterial({
        map: this._textures().marker,
        depthTest: false,
        transparent: true,
        sizeAttenuation: false,
      })
    );
    sprite.position.copy(point);
    sprite.scale.set(scale, scale, 1);
    sprite.renderOrder = 999;
    return sprite;
  }

  _worldPerPixel(point) {
    const dist = this.viewer.camera.position.distanceTo(point);
    const heightPx = this.viewer.renderer.domElement.clientHeight || 1;
    return (2 * dist * Math.tan((this.viewer.camera.fov * Math.PI) / 360)) / heightPx;
  }

  _bboxLen() {
    return this.viewer.bbox.getSize(new THREE.Vector3()).length() || 1;
  }

  setResolution(width, height) {
    this._res.set(Math.max(width, 1), Math.max(height, 1));
    const apply = (root) =>
      root.traverse((child) => {
        if (child.isLineSegments2) child.material.resolution.copy(this._res);
      });
    apply(this.group);
    apply(this.previewGroup);
    apply(this.pendingGroup);
  }

  // ---------- snapping ----------

  _edgesOf(geometry) {
    if (geometry.userData._snapEdges) return geometry.userData._snapEdges;
    const edges = [];
    const seen = new Set();
    const push = (a, b) => {
      const key = a < b ? a * 1e7 + b : b * 1e7 + a;
      if (seen.has(key)) return;
      seen.add(key);
      edges.push(a, b);
    };
    const index = geometry.index;
    if (index) {
      for (let i = 0; i + 2 < index.count; i += 3) {
        const a = index.getX(i);
        const b = index.getX(i + 1);
        const c = index.getX(i + 2);
        push(a, b);
        push(b, c);
        push(c, a);
      }
    } else {
      const count = geometry.attributes.position.count;
      for (let i = 0; i + 2 < count; i += 3) {
        push(i, i + 1);
        push(i + 1, i + 2);
        push(i + 2, i);
      }
    }
    geometry.userData._snapEdges = edges;
    return edges;
  }

  /** World-space vertex cache per mesh (explode only translates, so key on position). */
  _snapCache(mesh) {
    const e = mesh.matrixWorld.elements;
    const key = `${e[12].toFixed(6)}_${e[13].toFixed(6)}_${e[14].toFixed(6)}`;
    const cached = mesh.userData._snapCache;
    if (cached && cached.key === key) return cached;
    const position = mesh.geometry.attributes.position;
    const verts = new Float32Array(position.count * 3);
    const v = new THREE.Vector3();
    for (let i = 0; i < position.count; i += 1) {
      v.fromBufferAttribute(position, i).applyMatrix4(mesh.matrixWorld);
      verts[i * 3] = v.x;
      verts[i * 3 + 1] = v.y;
      verts[i * 3 + 2] = v.z;
    }
    const cache = { key, verts, edges: this._edgesOf(mesh.geometry) };
    mesh.userData._snapCache = cache;
    return cache;
  }

  /** OSNAP: nearest vertex wins, then nearest edge projection, else the raw face point. */
  _snap(hit) {
    const mesh = hit.object;
    if (!mesh?.geometry?.attributes?.position)
      return { point: hit.point.clone(), kind: "face" };
    const cache = this._snapCache(mesh);
    const p = hit.point;
    const wpp = this._worldPerPixel(p);
    const { verts, edges } = cache;

    const vThr = wpp * 16;
    let bestIndex = -1;
    let bestD2 = vThr * vThr;
    for (let i = 0; i < verts.length; i += 3) {
      const dx = verts[i] - p.x;
      const dy = verts[i + 1] - p.y;
      const dz = verts[i + 2] - p.z;
      const d2 = dx * dx + dy * dy + dz * dz;
      if (d2 < bestD2) {
        bestD2 = d2;
        bestIndex = i;
      }
    }
    if (bestIndex >= 0)
      return {
        point: new THREE.Vector3(verts[bestIndex], verts[bestIndex + 1], verts[bestIndex + 2]),
        kind: "vertex",
      };

    const eThr = wpp * 10;
    let bestPoint = null;
    let bestED = eThr;
    const a = new THREE.Vector3();
    const ab = new THREE.Vector3();
    const proj = new THREE.Vector3();
    for (let i = 0; i + 1 < edges.length; i += 2) {
      const ia = edges[i] * 3;
      const ib = edges[i + 1] * 3;
      a.set(verts[ia], verts[ia + 1], verts[ia + 2]);
      ab.set(verts[ib] - a.x, verts[ib + 1] - a.y, verts[ib + 2] - a.z);
      const len2 = ab.lengthSq();
      if (len2 < 1e-12) continue;
      let t =
        ((p.x - a.x) * ab.x + (p.y - a.y) * ab.y + (p.z - a.z) * ab.z) / len2;
      t = Math.min(Math.max(t, 0), 1);
      proj.copy(a).addScaledVector(ab, t);
      const d = proj.distanceTo(p);
      if (d < bestED) {
        bestED = d;
        bestPoint = proj.clone();
      }
    }
    if (bestPoint) return { point: bestPoint, kind: "edge" };
    return { point: p.clone(), kind: "face" };
  }

  _showSnap(snapped) {
    if (snapped.kind === "face") {
      this._hideSnap();
      return;
    }
    if (!this._snapSprite) {
      this._snapSprite = new THREE.Sprite(
        new THREE.SpriteMaterial({
          map: this._textures().snapVertex,
          depthTest: false,
          transparent: true,
          sizeAttenuation: false,
        })
      );
      this._snapSprite.scale.set(0.02, 0.02, 1);
      this._snapSprite.renderOrder = 1001;
      this.viewer.scene.add(this._snapSprite);
    }
    this._snapSprite.material.map =
      snapped.kind === "vertex" ? this._textures().snapVertex : this._textures().snapEdge;
    this._snapSprite.material.needsUpdate = true;
    this._snapSprite.position.copy(snapped.point);
    this._snapSprite.visible = true;
    this._dirty();
  }

  _hideSnap() {
    if (this._snapSprite) this._snapSprite.visible = false;
    this._dirty();
  }

  // ---------- laser boundary search ----------

  /** True when a probe from just above q still lands on the same surface plane. */
  _onSurface(q, n, off, targets) {
    this._probe.set(q.clone().addScaledVector(n, off), n.clone().negate());
    this._probe.near = 0;
    this._probe.far = off * 4;
    const hit = this._probe.intersectObjects(targets, false)[0];
    if (!hit?.face) return false;
    const wn = hit.face.normal.clone().transformDirection(hit.object.matrixWorld);
    return Math.abs(wn.dot(n)) > 0.7;
  }

  /** March + bisect along d (in-plane) to the real surface boundary; null if none. */
  _marchToBoundary(p, n, d, targets) {
    const mm = this.viewer.mmPerUnit || 1;
    const off = Math.max(2 / mm, this._bboxLen() * 1e-5);
    if (!this._onSurface(p, n, off, targets)) return null;
    const maxT = 300000 / mm; // 300 m safety cap
    const q = new THREE.Vector3();
    let lo = 0;
    let t = Math.max(8 / mm, this._bboxLen() * 1e-4);
    let hi = null;
    while (t <= maxT) {
      q.copy(p).addScaledVector(d, t);
      if (this._onSurface(q, n, off, targets)) {
        lo = t;
        t *= 2;
      } else {
        hi = t;
        break;
      }
    }
    if (hi === null) return null;
    for (let i = 0; i < 16; i += 1) {
      const mid = (lo + hi) / 2;
      q.copy(p).addScaledVector(d, mid);
      if (this._onSurface(q, n, off, targets)) lo = mid;
      else hi = mid;
    }
    return lo;
  }

  /**
   * Two perpendicular in-plane axes (aligned with the surface's own orientation,
   * slanted panels included) and the real boundary distance on each of the 4 sides.
   * Returns null when no reliable boundary is found — never fabricates a result.
   */
  _computeLaser(hit) {
    if (!hit?.face) return null;
    const mesh = hit.object;
    const n = hit.face.normal.clone().transformDirection(mesh.matrixWorld);
    if (n.lengthSq() < 1e-10) return null;
    n.normalize();

    const position = mesh.geometry.attributes.position;
    if (!position) return null;
    const corners = [hit.face.a, hit.face.b, hit.face.c].map((i) =>
      new THREE.Vector3().fromBufferAttribute(position, i).applyMatrix4(mesh.matrixWorld)
    );
    let u = null;
    let longest = -1;
    for (let i = 0; i < 3; i += 1) {
      const edge = corners[(i + 1) % 3].clone().sub(corners[i]);
      const len = edge.length();
      if (len > longest) {
        longest = len;
        u = edge.normalize();
      }
    }
    if (!u) return null;
    u.addScaledVector(n, -u.dot(n)); // project into the surface plane
    if (u.lengthSq() < 1e-10) return null;
    u.normalize();
    const v = new THREE.Vector3().crossVectors(n, u).normalize();

    const targets = this.viewer.probeTargets();
    if (!targets.length) return null;
    const p = hit.point.clone();
    const ts = [u, u.clone().negate(), v, v.clone().negate()].map((d) =>
      this._marchToBoundary(p, n, d, targets)
    );
    if (ts.some((t) => t == null)) return null;
    return { p, u, v, ts };
  }

  // ---------- previews ----------

  _clearPreview() {
    const group = this.previewGroup;
    [...group.children].forEach((child) => {
      if (child.userData.shared) {
        child.visible = false;
        return;
      }
      group.remove(child);
      child.geometry?.dispose();
      child.material?.dispose();
    });
    this._dirty();
  }

  _previewLabel(index, text, position) {
    const label = this._previewLabels[index];
    label.setText(text);
    label.sprite.position.copy(position);
    label.sprite.visible = true;
  }

  _updatePendingPreview(cursor) {
    this._clearPreview();
    const mm = this.viewer.mmPerUnit || 1;
    if (this.tool === "distance" && this.pending.length === 1) {
      const a = this.pending[0];
      this.previewGroup.add(this._lineSegments([a, cursor], PREVIEW));
      const length = a.distanceTo(cursor) * mm;
      this._previewLabel(
        0,
        formatLengthMM(length, this.settings),
        a.clone().add(cursor).multiplyScalar(0.5)
      );
    } else if (this.tool === "angle" && this.pending.length === 1) {
      this.previewGroup.add(this._lineSegments([this.pending[0], cursor], PREVIEW));
    } else if (this.tool === "angle" && this.pending.length === 2) {
      const [p1, vertex] = this.pending;
      this.previewGroup.add(this._lineSegments([p1, vertex], BLUE));
      this.previewGroup.add(this._lineSegments([vertex, cursor], PREVIEW));
      const arc = this._angleArc(p1, vertex, cursor);
      if (arc) {
        this.previewGroup.add(this._linePolyline(arc.points, PREVIEW));
        this._previewLabel(0, formatAngleDeg(arc.deg, this.settings), arc.labelAt);
      }
    }
    this._dirty();
  }

  _angleArc(p1, vertex, p2) {
    const v1 = p1.clone().sub(vertex);
    const v2 = p2.clone().sub(vertex);
    if (v1.lengthSq() < 1e-12 || v2.lengthSq() < 1e-12) return null;
    const deg = THREE.MathUtils.radToDeg(
      Math.atan2(new THREE.Vector3().crossVectors(v1, v2).length(), v1.dot(v2))
    );
    const e1 = v1.clone().normalize();
    const v2n = v2.clone().normalize();
    const e2 = v2n.clone().addScaledVector(e1, -v2n.dot(e1));
    if (e2.lengthSq() < 1e-10) return null; // collinear arms
    e2.normalize();
    const radius = Math.min(v1.length(), v2.length()) * 0.3;
    const rad = THREE.MathUtils.degToRad(deg);
    const points = [];
    for (let i = 0; i <= 40; i += 1) {
      const t = (i / 40) * rad;
      points.push(
        vertex
          .clone()
          .addScaledVector(e1, Math.cos(t) * radius)
          .addScaledVector(e2, Math.sin(t) * radius)
      );
    }
    const labelAt = vertex
      .clone()
      .addScaledVector(e1, Math.cos(rad / 2) * radius * 1.7)
      .addScaledVector(e2, Math.sin(rad / 2) * radius * 1.7);
    return { points, deg, labelAt, e1, e2, rad, radius };
  }

  _laserHover(hit) {
    this._hideSnap();
    const now = performance.now();
    if (now - this._laserAt < 90) return;
    this._laserAt = now;
    this._clearPreview();
    if (!hit) return;
    const laser = this._computeLaser(hit);
    if (!laser) return; // no fabricated preview
    this._buildLaserVisual(laser, this.previewGroup, PREVIEW, this._previewLabels);
    this._dirty();
  }

  /** Draws the two boundary-to-boundary segments; labels reused when provided. */
  _buildLaserVisual(laser, group, color, sharedLabels) {
    const mm = this.viewer.mmPerUnit || 1;
    const { p, u, v, ts } = laser;
    const a1 = p.clone().addScaledVector(u, -ts[0]);
    const a2 = p.clone().addScaledVector(u, ts[1]);
    const b1 = p.clone().addScaledVector(v, -ts[2]);
    const b2 = p.clone().addScaledVector(v, ts[3]);
    group.add(this._lineSegments([a1, a2, b1, b2], color));
    [a1, a2, b1, b2, p].forEach((point) => group.add(this._marker(point)));
    const lenA = formatLengthMM(a1.distanceTo(a2) * mm, this.settings);
    const lenB = formatLengthMM(b1.distanceTo(b2) * mm, this.settings);
    if (sharedLabels) {
      this._previewLabel(0, lenA, a1.clone().add(a2).multiplyScalar(0.5));
      this._previewLabel(1, lenB, b1.clone().add(b2).multiplyScalar(0.5));
      return null;
    }
    const labels = [new MeasureLabel(lenA), new MeasureLabel(lenB)];
    labels[0].sprite.position.copy(a1).add(a2).multiplyScalar(0.5);
    labels[1].sprite.position.copy(b1).add(b2).multiplyScalar(0.5);
    group.add(labels[0].sprite, labels[1].sprite);
    return labels;
  }

  // ---------- committed measurements ----------

  _addItem({ type, group, labels, values, texts }) {
    const id = this._nextId;
    this._nextId += 1;
    group.traverse((child) => {
      if (child.userData.label || child.userData.noPick) return;
      if (child.isSprite || child.isLineSegments2) {
        child.userData.measureId = id;
        child.userData.baseColor = child.material.color.getHex();
      }
    });
    this.group.add(group);
    this.items.set(id, { id, type, group, labels, values, texts });
    this.select(id);
    this._dirty();
  }

  _commitDistance(a, b) {
    const mm = this.viewer.mmPerUnit || 1;
    const value = a.distanceTo(b) * mm;
    const group = new THREE.Group();
    group.add(this._lineSegments([a, b], BLUE));
    group.add(this._marker(a));
    group.add(this._marker(b));
    const label = new MeasureLabel(formatLengthMM(value, this.settings));
    label.sprite.position.copy(a).add(b).multiplyScalar(0.5);
    group.add(label.sprite);
    this._addItem({
      type: "distance",
      group,
      labels: [label],
      values: [value],
      texts: (s) => [formatLengthMM(value, s)],
    });
  }

  _commitAngle(p1, vertex, p2) {
    const arc = this._angleArc(p1, vertex, p2);
    if (!arc) return;
    const group = new THREE.Group();
    group.add(this._lineSegments([p1, vertex, vertex, p2], BLUE));
    group.add(this._linePolyline(arc.points, BLUE));
    [p1, vertex, p2].forEach((point) => group.add(this._marker(point)));

    // Translucent wedge between the arms.
    const positions = [];
    for (let i = 0; i + 1 < arc.points.length; i += 1) {
      positions.push(
        vertex.x, vertex.y, vertex.z,
        arc.points[i].x, arc.points[i].y, arc.points[i].z,
        arc.points[i + 1].x, arc.points[i + 1].y, arc.points[i + 1].z
      );
    }
    const wedgeGeometry = new THREE.BufferGeometry();
    wedgeGeometry.setAttribute(
      "position",
      new THREE.Float32BufferAttribute(positions, 3)
    );
    const wedge = new THREE.Mesh(
      wedgeGeometry,
      new THREE.MeshBasicMaterial({
        color: BLUE,
        transparent: true,
        opacity: 0.12,
        side: THREE.DoubleSide,
        depthTest: false,
      })
    );
    wedge.renderOrder = 997;
    wedge.userData.noPick = true;
    group.add(wedge);

    const label = new MeasureLabel(formatAngleDeg(arc.deg, this.settings));
    label.sprite.position.copy(arc.labelAt);
    group.add(label.sprite);
    this._addItem({
      type: "angle",
      group,
      labels: [label],
      values: [arc.deg],
      texts: (s) => [formatAngleDeg(arc.deg, s)],
    });
  }

  _commitLaser(hit) {
    const laser = this._computeLaser(hit);
    if (!laser) {
      this.viewer.onMeasureNotice?.("laser-invalid");
      return;
    }
    const mm = this.viewer.mmPerUnit || 1;
    const { ts } = laser;
    const lenA = (ts[0] + ts[1]) * mm;
    const lenB = (ts[2] + ts[3]) * mm;
    const group = new THREE.Group();
    const labels = this._buildLaserVisual(laser, group, BLUE, null);
    this._addItem({
      type: "laser",
      group,
      labels,
      values: [lenA, lenB],
      texts: (s) => [formatLengthMM(lenA, s), formatLengthMM(lenB, s)],
    });
  }

  // ---------- interaction ----------

  _pickMeasurement(ndc) {
    if (!this.group.children.length) return null;
    const raycaster = this.viewer.raycaster;
    raycaster.setFromCamera(ndc, this.viewer.camera);
    const hits = raycaster.intersectObjects(this.group.children, true);
    const hit = hits.find(
      (h) => h.object.userData.measureId != null && !h.object.userData.noPick
    );
    return hit ? hit.object.userData.measureId : null;
  }

  handleClick(ndc) {
    if (!this.active) return;
    const measurementHit = this._pickMeasurement(ndc);
    if (measurementHit != null && !this.pending.length) {
      this.select(measurementHit === this.selectedId ? null : measurementHit);
      return;
    }
    const hit = this.viewer.pickHits(ndc)[0];
    if (!hit) return;
    if (this.tool === "laser") {
      this._commitLaser(hit);
      this._clearPreview();
      return;
    }
    const snapped = this._snap(hit);
    this._placePoint(snapped.point);
  }

  handleMove(ndc) {
    if (!this.active) return;
    const hit = this.viewer.pickHits(ndc)[0] || null;
    if (this.tool === "laser") {
      this._laserHover(hit);
      return;
    }
    if (!hit) {
      this._hideSnap();
      this._clearPreview();
      return;
    }
    const snapped = this._snap(hit);
    this._showSnap(snapped);
    if (this.pending.length) this._updatePendingPreview(snapped.point);
    else this._clearPreview();
  }

  _placePoint(point) {
    this.pending.push(point.clone());
    this.pendingGroup.add(this._marker(point));
    const needed = this.tool === "distance" ? 2 : 3;
    if (this.pending.length >= needed) {
      if (this.tool === "distance") this._commitDistance(this.pending[0], this.pending[1]);
      else this._commitAngle(this.pending[0], this.pending[1], this.pending[2]);
      this._clearPending();
      this._clearPreview();
    }
    this._dirty();
    this._emit();
  }

  _clearPending() {
    this.pending = [];
    [...this.pendingGroup.children].forEach((child) => {
      this.pendingGroup.remove(child);
      child.material?.dispose();
    });
  }

  /** ESC: drops only the unfinished measurement; committed ones stay. */
  cancelPending() {
    if (!this.pending.length) {
      this._clearPreview();
      return;
    }
    this._clearPending();
    this._clearPreview();
    this._emit();
  }

  select(id) {
    if (this.selectedId != null && this.items.has(this.selectedId))
      this._tint(this.items.get(this.selectedId), false);
    this.selectedId = id ?? null;
    if (this.selectedId != null && this.items.has(this.selectedId))
      this._tint(this.items.get(this.selectedId), true);
    this._dirty();
    this._emit();
  }

  _tint(item, on) {
    item.group.traverse((child) => {
      if (child.userData.measureId == null || child.userData.label) return;
      if (child.material?.color)
        child.material.color.set(on ? SELECTED : child.userData.baseColor);
    });
  }

  deleteSelected() {
    if (this.selectedId == null) return;
    const item = this.items.get(this.selectedId);
    if (item) this._disposeItem(item);
    this.selectedId = null;
    this._dirty();
    this._emit();
  }

  clearAll() {
    [...this.items.values()].forEach((item) => this._disposeItem(item));
    this.selectedId = null;
    this._clearPending();
    this._clearPreview();
    this._hideSnap();
    this._dirty();
    this._emit();
  }

  _disposeItem(item) {
    this.group.remove(item.group);
    item.group.traverse((child) => {
      child.geometry?.dispose();
      if (child.material && !child.userData.label) child.material.dispose();
    });
    (item.labels || []).forEach((label) => label.dispose());
    this.items.delete(item.id);
  }

  setTool(tool) {
    if (this.tool === tool) return;
    this.tool = tool;
    this._clearPending();
    this._clearPreview();
    this._hideSnap();
    this._emit();
  }

  setActive(on) {
    this.active = on;
    if (!on) {
      this._clearPending();
      this._clearPreview();
      this._hideSnap();
    }
    this._emit();
  }

  setSettings(settings) {
    this.settings = { ...this.settings, ...settings };
    this.items.forEach((item) => {
      const texts = item.texts(this.settings);
      item.labels.forEach((label, index) => label.setText(texts[index] || ""));
    });
    this._dirty();
    this._emit();
  }

  _emit() {
    const selected = this.selectedId != null ? this.items.get(this.selectedId) : null;
    this.viewer.onMeasureChange?.({
      tool: this.tool,
      count: this.items.size,
      pending: this.pending.length,
      selectedId: this.selectedId,
      selectedText: selected ? selected.texts(this.settings).join(" × ") : null,
    });
  }

  _dirty() {
    this.viewer._markDirty();
  }

  dispose() {
    this.clearAll();
    Object.values(this._tex || {}).forEach((texture) => texture.dispose());
    this._previewLabels.forEach((label) => label.dispose());
    if (this._snapSprite) {
      this.viewer.scene.remove(this._snapSprite);
      this._snapSprite.material.dispose();
    }
    this.viewer.scene.remove(this.group, this.previewGroup, this.pendingGroup);
  }
}
